// Preview-mode store for bidirectional company associations.
// Persists per-company associations in memory (session-scoped) and
// mirrors the reverse relationship on the other company's card.
//
// parent <-> subsidiary, partner <-> partner

export type AssociationRelationship = "parent" | "subsidiary" | "partner" | "other";

export interface AssociatedCompanyEntry {
  id: string;
  name: string;
  domain?: string | null;
  logoUrl?: string | null;
  relationship?: AssociationRelationship;
}

export function reverseRelationship(
  rel: AssociationRelationship | undefined,
): AssociationRelationship | undefined {
  if (rel === "parent") return "subsidiary";
  if (rel === "subsidiary") return "parent";
  if (rel === "partner") return "partner";
  if (rel === "other") return "other";
  return undefined;
}

// Minimal structural shapes for the two backend association sources, so the
// GraphQL-generated types satisfy them without importing them here.
interface AccountLike {
  id?: string | null;
  domain?: string | null;
  properties?: { name?: string | null; logoUrl?: string | null } | null;
}
interface LabeledAssociationLike {
  label?: string | null;
  toAccount?: AccountLike | null;
}

function toEntry(acc: AccountLike, relationship?: AssociationRelationship): AssociatedCompanyEntry {
  return {
    id: acc.id as string,
    name: acc.properties?.name || acc.domain || "Unknown",
    domain: acc.domain ?? undefined,
    logoUrl: acc.properties?.logoUrl ?? null,
    relationship,
  };
}

/**
 * Merge the two backend association sources into the display list, deduped by id.
 *
 * `associatedAccounts` is the legacy, unlabeled store (e.g. CRM-ingested parent/
 * child links); `accountAssociations` is the new labeled store (relationship in
 * `label`, linked account in `toAccount`). We union both so legacy associations
 * keep showing, and labeled entries win on id collision because they carry the
 * relationship. Once the backend backfills legacy links into the labeled store,
 * the legacy source can be dropped and this becomes `accountAssociations` only.
 */
export function mergeAssociatedCompanies(
  legacy: ReadonlyArray<AccountLike | null | undefined> | null | undefined,
  labeled: ReadonlyArray<LabeledAssociationLike | null | undefined> | null | undefined,
): AssociatedCompanyEntry[] {
  const byId = new Map<string, AssociatedCompanyEntry>();
  for (const a of legacy ?? []) {
    if (!a?.id) continue;
    byId.set(a.id, toEntry(a));
  }
  for (const e of labeled ?? []) {
    const acc = e?.toAccount;
    if (!acc?.id) continue;
    byId.set(acc.id, toEntry(acc, (e?.label || undefined) as AssociationRelationship | undefined));
  }
  return Array.from(byId.values());
}

type CompanyRef = { id: string; name: string; domain?: string | null; logoUrl?: string | null };

class CompanyAssociationsStore {
  private map = new Map<string, AssociatedCompanyEntry[]>();
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit() {
    this.listeners.forEach((l) => l());
  }

  get(companyId: string): AssociatedCompanyEntry[] | undefined {
    return this.map.get(companyId);
  }

  seed(companyId: string, entries: AssociatedCompanyEntry[]) {
    if (!this.map.has(companyId)) {
      this.map.set(companyId, entries);
    }
  }

  // Replace associations for `self`, syncing reverse entries for any adds/removes.
  set(self: CompanyRef, next: AssociatedCompanyEntry[]) {
    const prev = this.map.get(self.id) ?? [];
    const prevIds = new Set(prev.map((e) => e.id));
    const nextIds = new Set(next.map((e) => e.id));

    // Removed associations -> remove reverse entries pointing back to self
    prev.forEach((e) => {
      if (!nextIds.has(e.id)) {
        const other = this.map.get(e.id) ?? [];
        this.map.set(
          e.id,
          other.filter((o) => o.id !== self.id),
        );
      }
    });

    // Added or updated associations -> upsert reverse entry
    next.forEach((e) => {
      const prevEntry = prev.find((p) => p.id === e.id);
      const relChanged = !prevEntry || prevEntry.relationship !== e.relationship;
      if (!prevIds.has(e.id) || relChanged) {
        const reverseRel = reverseRelationship(e.relationship);
        const other = this.map.get(e.id) ?? [];
        const withoutSelf = other.filter((o) => o.id !== self.id);
        withoutSelf.push({
          id: self.id,
          name: self.name,
          domain: self.domain ?? null,
          logoUrl: self.logoUrl ?? null,
          relationship: reverseRel,
        });
        this.map.set(e.id, withoutSelf);
      }
    });

    this.map.set(self.id, next);
    this.emit();
  }
}

export const companyAssociationsStore = new CompanyAssociationsStore();